// ══════════════════════════════════════════════════════════════════
//  ED CRM - Cadence Service (núcleo/aplicação)
// ══════════════════════════════════════════════════════════════════
//  Wrappers em volta da RPC trigger_stage_cadence + utilitarios para
//  navegar pela ordem dos stages do pipeline.
//  Recebe o repositório via porta (DI) — não conhece Supabase.
// ══════════════════════════════════════════════════════════════════

import type { CadenceStep } from '@models';
import type { ICadenceRepository, NextStage } from '../ports/ICadenceRepository';

// Re-exporta tipos de resultado consumidos pela UI.
export type { NextStage } from '../ports/ICadenceRepository';

export interface TriggerStageCadenceParams {
  cardId: string;
  stageId: string;
  assignedTo?: string | null;
}

export class CadenceService {
  constructor(private readonly repo: ICadenceRepository) {}

  // Dispara a cadencia configurada no pipeline para o stage atual.
  // Cancela pendentes do card e cria as novas. Retorna { created, cancelled }.
  triggerStageCadence = async ({
    cardId,
    stageId,
    assignedTo = null,
  }: TriggerStageCadenceParams): Promise<unknown> => {
    if (!cardId || !stageId) return null;
    return this.repo.triggerStageCadence({ cardId, stageId, assignedTo });
  };

  // Busca o proximo stage (order_index > atual) do mesmo pipeline.
  // Usado pelo dialog "Contato Efetivado" para descobrir para onde mover.
  getNextStage = async (cardId: string | null | undefined): Promise<NextStage | null> => {
    if (!cardId) return null;
    const card = await this.repo.getCardStageInfo(cardId);
    if (!card?.pipeline_id) return null;
    return this.repo.getNextStage(card.pipeline_id, card.current_order);
  };

  // Le e atualiza a cadencia inteira do pipeline. Forma esperada do array:
  // [{ stage_name, actions: [{ type, title, description?, script?, delay_hours }] }]
  getPipelineCadence = async (pipelineId: string): Promise<CadenceStep[]> => {
    return this.repo.getPipelineCadence(pipelineId);
  };

  setPipelineCadence = async (
    pipelineId: string,
    cadence: CadenceStep[] | unknown,
  ): Promise<void> => {
    const safe = Array.isArray(cadence) ? (cadence as CadenceStep[]) : [];
    await this.repo.setPipelineCadence(pipelineId, safe);
  };
}
